import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardFooter, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { UserProfile } from "@shared/types";
import { loginUser, resendVerificationCode, verifyEmailCode } from "@/lib/supabase";

interface LoginProps {
  onLogin: (user: UserProfile) => void;
  sessionExpired?: boolean;
}

export default function Login({ onLogin, sessionExpired = false }: LoginProps) {
  const demoEmail = import.meta.env.VITE_DEMO_LOGIN_EMAIL;
  const demoPassword = import.meta.env.VITE_DEMO_LOGIN_PASSWORD;
  const demoLoginEnabled = import.meta.env.VITE_DEMO_LOGIN_ENABLED === 'true' && !!demoEmail && !!demoPassword;
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationEmail, setVerificationEmail] = useState("");
  const [pendingPassword, setPendingPassword] = useState("");
  const [verificationStep, setVerificationStep] = useState(false);
  const [fullName, setFullName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      if (isLogin) {
        // Login with real authentication
        console.log('Attempting login with:', email);
        const response = await loginUser(email, password);
        
        if (response.requiresVerification) {
          setVerificationStep(true);
          setVerificationEmail(response.verificationEmail || email);
          setPendingPassword(password);
          toast({
            title: "Verification Required",
            description: "Enter the email verification code to activate this account.",
            variant: "destructive",
          });
          return;
        }

        if (response.error) {
          throw new Error(response.error);
        }
        
        if (response.user) {
          toast({
            title: "Login Successful",
            description: `Welcome back, ${response.user.fullName}!`,
          });
          onLogin(response.user);
        } else {
          throw new Error("No user data received");
        }
      } else {
        // Registration not enabled yet
        toast({
          title: "Registration not enabled",
          description: "Please contact your administrator for an account",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error('Login error:', error);
      toast({
        title: isLogin ? "Login Failed" : "Registration Failed",
        description: error.message || "Please check your credentials and try again",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setIsLoading(true);
    try {
      const result = await verifyEmailCode(verificationEmail || email, verificationCode);
      if (!result.success) {
        throw new Error(result.error || "Invalid verification code");
      }

      const login = await loginUser(verificationEmail || email, pendingPassword || password);
      if (login.error || !login.user) {
        throw new Error(login.error || "Verified, but sign-in failed. Please log in again.");
      }

      toast({
        title: "Email Verified",
        description: "Your account is now active.",
      });
      setVerificationStep(false);
      setVerificationCode("");
      onLogin(login.user);
    } catch (error: any) {
      toast({
        title: "Verification Failed",
        description: error.message || "Could not verify code",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsLoading(true);
    try {
      const result = await resendVerificationCode(verificationEmail || email);
      if (!result.success) {
        throw new Error(result.error || "Failed to resend code");
      }
      toast({
        title: "Code Sent",
        description: "A new verification code has been sent.",
      });
    } catch (error: any) {
      toast({
        title: "Resend Failed",
        description: error.message || "Could not resend code",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDemoLogin = async () => {
    if (!demoLoginEnabled) {
      toast({
        title: "Demo Login Disabled",
        description: "Demo login is not configured for this environment.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await loginUser(demoEmail, demoPassword);

      if (response.error || !response.user) {
        throw new Error(response.error || 'Demo login failed');
      }
      
      toast({
        title: "Demo Login Successful",
        description: `Welcome back, ${response.user.fullName}!`,
      });
      
      onLogin(response.user);
    } catch (error: any) {
      toast({
        title: "Demo Login Failed",
        description: error.message || "Please try again",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-900 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-2">
            <div className="flex items-center">
              <span className="mdi mdi-eye-outline text-primary text-4xl mr-2"></span>
              <h1 className="text-2xl font-bold">BoltOS</h1>
            </div>
          </div>
          <CardTitle className="text-xl">
            {isLogin ? "Sign in to your account" : "Create a new account"}
          </CardTitle>
          <CardDescription>
            {isLogin 
              ? "Enter your credentials to access your dashboard" 
              : "Fill out the form below to get started"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sessionExpired && (
            <p role="status" className="mb-4 text-sm text-neutral-700 dark:text-neutral-200">
              Your session expired. Sign in again to return to this page.
            </p>
          )}
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input 
                    id="name" 
                    type="text" 
                    placeholder="John Doe" 
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required={!isLogin} 
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input 
                  id="email" 
                  type="email" 
                  placeholder="name@example.com" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required 
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  {isLogin && (
                    <Button variant="link" className="h-auto p-0 text-xs" type="button">
                      Forgot password?
                    </Button>
                  )}
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pr-12"
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    aria-controls="password"
                    onClick={() => setShowPassword((visible) => !visible)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
                  </Button>
                </div>
              </div>
              
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <span className="flex items-center">
                    <span className="mdi mdi-loading mdi-spin mr-2"></span>
                    {isLogin ? "Signing in..." : "Creating account..."}
                  </span>
                ) : (
                  isLogin ? "Sign In" : "Create Account"
                )}
              </Button>

              {verificationStep && (
                <>
                  <div className="rounded border border-amber-400/40 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm">
                    This account needs email verification. Enter the code sent to <strong>{verificationEmail || email}</strong>.
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="verificationCode">Verification Code</Label>
                    <Input
                      id="verificationCode"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      placeholder="6-digit code"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Button type="button" variant="outline" onClick={handleResendCode} disabled={isLoading}>
                      Resend Code
                    </Button>
                    <Button type="button" onClick={handleVerifyCode} disabled={isLoading || !verificationCode.trim()}>
                      Verify Code
                    </Button>
                  </div>
                </>
              )}
            </div>
          </form>
          
          <div className="mt-4">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <Separator />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  Or
                </span>
              </div>
            </div>
            
            {demoLoginEnabled && (
            <div className="mt-4">
              <Button 
                variant="outline" 
                className="w-full" 
                type="button"
                formNoValidate
                disabled={isLoading}
                onClick={handleDemoLogin}
              >
                <span className="mdi mdi-account-arrow-right mr-2"></span>
                Demo Login
              </Button>
            </div>
            )}
          </div>
        </CardContent>
        <CardFooter className="flex flex-col gap-2">
          <div className="text-center text-sm">
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <Button 
              variant="link" 
              className="h-auto p-0" 
              type="button"
              onClick={() => setIsLogin(!isLogin)}
            >
              {isLogin ? "Sign up" : "Sign in"}
            </Button>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}
